import { Timestamps } from '@/api/global/types/timestamps'
import { Subtask } from '@/api/pathways/subtasks/types'
import { IdMap } from '@/legacy/types/api/store'

import {
  TaskStatus,
  StatusReason,
  TemplateStatus,
} from '@/legacy/types/pathways/pathways'

import {
  SubtaskPriority,
  SubtaskTemplate,
} from '@/legacy/types/pathways/subtasks'
import { UUID } from '@/libs/typing/base'
import { CommonTemplateBase, StatusReasonMetadata } from './common'

// TYPES --------------------
export type GetTasksParams = {
  filter_patient_ids: UUID[] | null
  filter_task_ids?: UUID[] | null
  filter_pathway_ids?: UUID[] | null
  filter_statuses?: TaskStatus[] | null
  page_length?: number | null
  page_number?: number | null
  filter_free_text?: string | null
}

export type CreateTaskInfo = {
  variantId: string
  dueDatetime: string
  priority?: SubtaskPriority
  customSubject?: string | null
}

export type TaskPayload = {
  status: TaskStatus
  goalIds?: string[]
}

export type MapTaskSubtask = {
  mappingId: UUID
  mappingTemplateId: UUID
  subtaskId: UUID
  taskId: UUID
  sortNumber: number
}

export type MapTaskSubtaskTemplate = {
  mappingTemplateId: UUID
  subtaskVariantId: UUID
  taskVariantId: UUID
  sortNumber: number
}

export type ApiTask = {
  description: string | null
  dueDatetime: string
  minStartDatetime: string
  memberId: string
  status: TaskStatus
  statusReason: StatusReason
  statusUpdatedAt: string
  taskId: string
  taskVariantId: string
  title: string
  subtasks?: Subtask[]
  subtaskMaps?: MapTaskSubtask[]
  subtaskIds?: string[]
  taskTemplate?: TaskTemplate
  pathwayIds: string[]
} & Timestamps

export type Task = {
  description: string | null
  dueDatetime: string
  minStartDatetime: string
  memberId: string
  status: TaskStatus
  statusReason: StatusReason | null
  statusReasonJson: StatusReasonMetadata | null
  statusUpdatedAt: string
  taskId: string
  taskVariantId: string
  title: string
  generatingTriggerId: UUID | null
  subtasks?: Subtask[]
  subtaskIds?: string[]
  subtaskMaps?: MapTaskSubtask[]
  taskTemplate?: TaskTemplate
  pathwayIds: string[]
  goalIds: string[]
} & Timestamps

export type CreateTasksAndLinkCommunicationRequest = {
  tasks: CreateTaskInfo[]
  communicationId: string
}

export type CreateTasksAndLinkCommunicationResponse = {
  tasksCreated: Task[]
  subtasksLinked: Subtask[]
  tasksNotCreated?: Task[]
  subtasksNotLinked?: Subtask[]
}

export type TaskTemplate = {
  baseTemplateId: string
  description: string
  isDeleted?: boolean
  legacyActivityCategoryId: string
  subtaskTemplates?: SubtaskTemplate[]
  subtaskTemplateMaps?: MapTaskSubtaskTemplate[]
  taskKey: string
  taskTemplateId: string
  templateStatus: TemplateStatus
  title: string
  variantId: string
  variantKey: string | null
  variantDisplay: string
  isManuallyAddable: boolean
} & Timestamps

export type TaskTemplateBase = {
  taskKey: string
  variants?: TaskTemplate[]
} & CommonTemplateBase

// INTERFACES --------------------
export interface TaskState {
  isLoading: boolean
  error: Error | null
  data: IdMap<Task> | null
  isLoaded: boolean
}

export interface TaskTemplateDataState {
  isLoading: boolean
  error: Error | null
  data: IdMap<TaskTemplate>
  isLoaded: boolean
  queryMetadata: {
    total: number
  } | null
}

// ENUMS --------------------
export enum TaskType {
  Subtask = 'subtasks',
  Task = 'tasks',
}

// CONSTANTS --------------------
export const MAX_NUM_TASKS = 10
